/**
 * FocusedTagContext — Equipment AI's shared sensor-visibility + focus state.
 *
 * Centralizes three concerns that previously lived in LocationSensorSection's
 * local state, plus two new ones driven by the model's <storage-key> sensor
 * citations:
 *
 *   - hiddenTags: session-scoped per-mount dismissals (lifted from
 *     LocationSensorSection so the chat panel can read them too).
 *   - aiAddedTags: tags the model cited that aren't in the asset's default
 *     or pinned set — session-scoped additions that dock alongside the
 *     existing chart grid. Carries the resolved metadata payload so we can
 *     render description/units without round-tripping.
 *   - aiTouchedTags: union of every storage_key the AI has cited in this
 *     session. Drives the persistent blue stripe on TagChartCell so the
 *     user can scan the dashboard and see what the model has been working
 *     on across the whole conversation, not just the last turn.
 *   - focusedTag: transient — set by a chip click, cleared after the
 *     pulse-and-scroll completes. TagChartCell subscribes and runs the
 *     existing buildHighlightPulseSx animation against the matching cell.
 *
 * Scope is per-Equipment-AI-page-mount. Providers are placed at the page
 * root of both EquipmentAI.tsx (PI/asset mode) and LocationDetail.tsx (CSV
 * mode). Each mount carries its own boundSourceId — storage-key citations
 * referring to a different source still render their chip (metadata is
 * useful), but click is a no-op (no chart canvas to focus on).
 *
 * Consumers outside the Equipment AI tree get the no-op default value, so
 * a <storage-key> chip rendered elsewhere shows metadata but does nothing
 * on click.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/** Shape of the JSON payload embedded in <storage-key>...</storage-key>
 * after backend resolution (see mre/utils/chat.py process_storage_key_refs).
 * The frontend treats every field as best-effort except source_id and
 * storage_key. */
export interface ResolvedStorageKeyPayload {
  source_id: number;
  source_name?: string | null;
  storage_key: string;
  description?: string | null;
  units?: string | null;
  value_column?: string | null;
}

export interface FocusedTagContextValue {
  /** The TimeSeriesSource this page's dashboard is rendering against. AI
   * citations whose source_id doesn't match a bound source (this or
   * roundsSourceId) are treated as "foreign" — the chip still renders,
   * but a click is a no-op. Null in contexts with no bound dashboard
   * (e.g. non-Equipment-AI consumers). */
  boundSourceId: number | null;

  /** The operator-rounds TimeSeriesSource (manual_readings slot on the
   * EquipmentAIConfig), when one is wired. Citations from this source are
   * accepted alongside boundSourceId — an AI-cited rounds reading docks
   * into the same sensor grid as cited PI sensors, rendered through the
   * per-tag AiAddedTagChart path with the rounds convention columns.
   * The AI-added/touched maps stay keyed by bare storage_key: rounds tag
   * ids (site:tour:sta) and PI storage keys are disjoint namespaces, and
   * each payload carries its source_id for consumers that must branch. */
  roundsSourceId: number | null;

  /** Session-scoped hidden set — close button on a chart adds to this.
   * Lifted from LocationSensorSection's local useState. */
  hiddenTags: Set<string>;
  hideTag: (tag: string) => void;
  showTag: (tag: string) => void;

  /** AI-cited tags that weren't in the dashboard's defaults+pinned set,
   * session-scoped. Map carries the resolved payload so we can render
   * description/units on the AI-added card without re-fetching. */
  aiAddedTags: Map<string, ResolvedStorageKeyPayload>;
  /** Remove an AI-added tag from the session (close button on AI-added
   * card). Persistent pin promotion uses a separate path that hits the
   * SensorOverride API. */
  removeAiAddedTag: (storageKey: string) => void;

  /** Every storage_key the AI has cited in this session that belongs to
   * the dashboard's bound source, mapped to its resolved payload.
   * Driven by StorageKeyLink mount (passive — every chip rendered grows
   * the map), not gated on the user clicking. Persists across chat turns
   * within the same page mount; never shrinks until unmount.
   *
   * Carries the payload (not just the key) so the AI-referenced lens can
   * PLOT a referenced sensor that isn't on the base dashboard — same
   * source guarantees the section's columns apply, and the description/
   * units render the card without a round-trip. Clicking a chip is the
   * opt-in that also docks the sensor in the All view (aiAddedTags); the
   * AI lens shows everything referenced whether clicked or not.
   *
   * Cross-source citations are NOT added here — a foreign storage_key
   * that happens to share a name with a local one would create a
   * false-positive stripe (and couldn't be plotted against this
   * dashboard's source anyway). */
  aiTouchedTags: Map<string, ResolvedStorageKeyPayload>;
  /** Passive marker — called by StorageKeyLink on mount for every chip
   * the resolver lets through. Gated on payload.source_id ===
   * boundSourceId. Idempotent. */
  markAsTouched: (payload: ResolvedStorageKeyPayload) => void;

  /** Currently-focused tag — TagChartCell subscribes and pulses+scrolls
   * the matching cell. Cleared by clearFocus after the pulse window. */
  focusedTag: { sourceId: number; storageKey: string } | null;
  /** Invoked by a <storage-key> chip click. Internally:
   *   1. Marks tag as AI-touched (stripe).
   *   2. If foreign-source, returns early.
   *   3. Removes from hiddenTags if present (un-dismiss).
   *   4. Adds to aiAddedTags if not in defaults/pinned.
   *   5. Sets focusedTag → TagChartCell pulses+scrolls. */
  requestFocus: (payload: ResolvedStorageKeyPayload) => void;
  clearFocus: () => void;

  /** Drop every AI-driven reference for this session: the AI-touched set
   * (blue stripes / chips / "AI-referenced" lens), the AI-added cards, and
   * any transient focus. Used by the chat's "New chat" action so a fresh
   * conversation doesn't inherit the previous one's sensor annotations. The
   * user's own dismissals (hiddenTags) and persistent pins are left intact;
   * LocationSensorSection's effect snaps the view back to "all" once
   * aiTouchedTags empties. */
  clearAiReferences: () => void;
}

const NOOP_VALUE: FocusedTagContextValue = {
  boundSourceId: null,
  roundsSourceId: null,
  hiddenTags: new Set(),
  hideTag: () => {},
  showTag: () => {},
  aiAddedTags: new Map(),
  removeAiAddedTag: () => {},
  aiTouchedTags: new Map(),
  markAsTouched: () => {},
  focusedTag: null,
  requestFocus: () => {},
  clearFocus: () => {},
  clearAiReferences: () => {},
};

const FocusedTagContext = createContext<FocusedTagContextValue>(NOOP_VALUE);

export interface FocusedTagProviderProps {
  /** The TimeSeriesSource this dashboard renders against. Null while the
   * config is still loading; the provider remains mounted but click
   * actions on chips are deferred no-ops until it resolves. */
  boundSourceId: number | null;
  /** Optional operator-rounds source (manual_readings slot). When set,
   * chips citing this source are accepted like boundSourceId ones —
   * marked as touched, session-added, and focused. Omit/null for
   * dashboards with no rounds slot (or where rounds docking is
   * deliberately disabled, e.g. the per-panel providers around the
   * rounds level grids in LocationDetail). */
  roundsSourceId?: number | null;
  /** On chat resume, the sensors the author had hidden (from the dialogue's
   * persisted `view_context.hidden_sensor_tags`). Seeds `hiddenTags` exactly
   * once so a shared chat reopens with the same sensors charted; undefined for
   * fresh chats (nothing to restore). Applied via effect (not the useState
   * initializer) because the resume dialogue resolves asynchronously, after
   * this provider has mounted. */
  initialHiddenTags?: string[];
  children: React.ReactNode;
}

export const FocusedTagProvider: React.FC<FocusedTagProviderProps> = ({
  boundSourceId,
  roundsSourceId = null,
  initialHiddenTags,
  children,
}) => {
  const [hiddenTags, setHiddenTags] = useState<Set<string>>(new Set());

  // Seed the resumed chat's hidden set, once. The section reads `hiddenTags`
  // from this context to filter `visibleTags`, so seeding here restores the
  // plotted sensors; the section's own onHiddenTagsChange effect then mirrors
  // it back into the chat's outgoing view_context. Guarded so a later prop
  // change (the dialogue arriving after mount) seeds exactly once and never
  // clobbers the user's own subsequent show/hide toggles.
  const hiddenSeededRef = useRef(false);
  useEffect(() => {
    if (hiddenSeededRef.current) return;
    if (!initialHiddenTags || initialHiddenTags.length === 0) return;
    hiddenSeededRef.current = true;
    setHiddenTags(new Set(initialHiddenTags));
  }, [initialHiddenTags]);
  const [aiAddedTags, setAiAddedTags] = useState<
    Map<string, ResolvedStorageKeyPayload>
  >(new Map());
  const [aiTouchedTags, setAiTouchedTags] = useState<
    Map<string, ResolvedStorageKeyPayload>
  >(new Map());
  const [focusedTag, setFocusedTag] = useState<{
    sourceId: number;
    storageKey: string;
  } | null>(null);

  const hideTag = useCallback((tag: string) => {
    setHiddenTags((prev) => {
      if (prev.has(tag)) return prev;
      const next = new Set(prev);
      next.add(tag);
      return next;
    });
  }, []);

  const showTag = useCallback((tag: string) => {
    setHiddenTags((prev) => {
      if (!prev.has(tag)) return prev;
      const next = new Set(prev);
      next.delete(tag);
      return next;
    });
  }, []);

  const removeAiAddedTag = useCallback((storageKey: string) => {
    setAiAddedTags((prev) => {
      if (!prev.has(storageKey)) return prev;
      const next = new Map(prev);
      next.delete(storageKey);
      return next;
    });
  }, []);

  // A payload is "local" when its source is one of the dashboard's bound
  // sources: the continuous PI source or (when wired) the operator-rounds
  // source. Everything else is a foreign citation — chip renders, click
  // is a no-op.
  const isBoundSource = useCallback(
    (sourceId: number) =>
      (boundSourceId != null && sourceId === boundSourceId) ||
      (roundsSourceId != null && sourceId === roundsSourceId),
    [boundSourceId, roundsSourceId],
  );

  // Passive marker — fired by StorageKeyLink on mount for every chip
  // the backend lets through. Gated on the chip's source matching a
  // bound source so a foreign-source mention of a same-named storage_key
  // doesn't create a false-positive stripe on a local sensor.
  const markAsTouched = useCallback(
    (payload: ResolvedStorageKeyPayload) => {
      if (!isBoundSource(payload.source_id)) return;
      setAiTouchedTags((prev) => {
        if (prev.has(payload.storage_key)) return prev;
        const next = new Map(prev);
        next.set(payload.storage_key, payload);
        return next;
      });
    },
    [isBoundSource],
  );

  const requestFocus = useCallback(
    (payload: ResolvedStorageKeyPayload) => {
      // 1. Persistent AI-touched marker for the matching-source case.
      //    Foreign-source clicks intentionally don't pollute the set —
      //    same rule markAsTouched enforces.
      markAsTouched(payload);

      // 2. Foreign-source citation: render chip metadata, no scroll.
      if (!isBoundSource(payload.source_id)) {
        return;
      }

      // 3. Un-dismiss if user previously closed this tag.
      setHiddenTags((prev) => {
        if (!prev.has(payload.storage_key)) return prev;
        const next = new Set(prev);
        next.delete(payload.storage_key);
        return next;
      });

      // 4. Session-add when not already in defaults/pinned. The
      //    LocationSensorSection merges aiAddedTags into the effective
      //    tag set, so duplicates with default/pinned are harmless —
      //    the dedupe happens at render time, not at insert time.
      setAiAddedTags((prev) => {
        if (prev.has(payload.storage_key)) return prev;
        const next = new Map(prev);
        next.set(payload.storage_key, payload);
        return next;
      });

      // 5. Trigger pulse+scroll. TagChartCell auto-clears after the
      //    pulse window via clearFocus.
      setFocusedTag({
        sourceId: payload.source_id,
        storageKey: payload.storage_key,
      });
    },
    [isBoundSource, markAsTouched],
  );

  const clearFocus = useCallback(() => setFocusedTag(null), []);

  // Clear only the AI-driven references; leave the user's dismissals
  // (hiddenTags) and persistent pins untouched. Guards avoid emitting a new
  // empty collection when one is already empty (no needless re-render).
  const clearAiReferences = useCallback(() => {
    setAiAddedTags((prev) => (prev.size === 0 ? prev : new Map()));
    setAiTouchedTags((prev) => (prev.size === 0 ? prev : new Map()));
    setFocusedTag((prev) => (prev === null ? prev : null));
  }, []);

  const value = useMemo<FocusedTagContextValue>(
    () => ({
      boundSourceId,
      roundsSourceId,
      hiddenTags,
      hideTag,
      showTag,
      aiAddedTags,
      removeAiAddedTag,
      aiTouchedTags,
      markAsTouched,
      focusedTag,
      requestFocus,
      clearFocus,
      clearAiReferences,
    }),
    [
      boundSourceId,
      roundsSourceId,
      hiddenTags,
      hideTag,
      showTag,
      aiAddedTags,
      removeAiAddedTag,
      aiTouchedTags,
      markAsTouched,
      focusedTag,
      requestFocus,
      clearFocus,
      clearAiReferences,
    ],
  );

  return (
    <FocusedTagContext.Provider value={value}>
      {children}
    </FocusedTagContext.Provider>
  );
};

export const useFocusedTag = (): FocusedTagContextValue => {
  return useContext(FocusedTagContext);
};

/** Color token for AI-touched / AI-added visual treatment. Blue sits
 * outside the nuclear health palette (red/yellow/green/orange), so it
 * can't be confused with alarm/warning/normal/maintenance states drawn
 * on chart canvases. Matches MUI's default ``primary.main`` so the AI
 * accent visually pairs with the rest of the app chrome. */
export const AI_ACCENT_COLOR = "#1976d2";
